/**
 * QueryLogs Page - Admin dashboard for viewing user queries and LLM responses
 *
 * Displays a filterable, paginated table of chat interactions from CosmosDB.
 * Supports filtering by date range and user.
 */

import React, { useState, useEffect, useCallback } from "react";
import { Spinner, SpinnerSize } from "@fluentui/react";
import {
  ArrowSyncRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  FilterRegular,
} from "@fluentui/react-icons";
import { AppHeader } from "@shared/components";
import { usePermissions } from "@shared/auth";
import { getQueryLogs } from "../api";
import type { QueryLogsResponse, QueryLogEntry, QueryLogsParams, Citation } from "../api";
import styles from "./QueryLogs.module.css";

/**
 * Format date for display (DD/MM/YYYY HH:MM)
 */
function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return dateString;
  }
}

/**
 * Format response time for display
 */
function formatResponseTime(ms: number): string {
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(1)}s`;
  }
  return `${ms}ms`;
}

/**
 * Truncate text with ellipsis
 */
function truncateText(text: string, maxLength: number): string {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
}

/**
 * Format date for input field (YYYY-MM-DD)
 */
function formatDateForInput(date: Date): string {
  return date.toISOString().split("T")[0];
}

/**
 * Expert mode badge component
 */
const ModeBadge: React.FC<{ expertMode: boolean }> = ({ expertMode }) => (
  <span className={`${styles.badge} ${expertMode ? styles.badgeExpert : styles.badgeStandard}`}>
    {expertMode ? "Expert" : "Standard"}
  </span>
);

/**
 * Status badge component
 */
const StatusBadge: React.FC<{
  wasBlocked: boolean;
  wasClarification: boolean;
  hasError: boolean;
}> = ({ wasBlocked, wasClarification, hasError }) => {
  if (wasBlocked) {
    return <span className={`${styles.badge} ${styles.badgeBlocked}`}>Blocked</span>;
  }
  if (hasError) {
    return <span className={`${styles.badge} ${styles.badgeError}`}>Error</span>;
  }
  if (wasClarification) {
    return <span className={`${styles.badge} ${styles.badgeClarification}`}>Clarification</span>;
  }
  return <span className={`${styles.badge} ${styles.badgeOk}`}>OK</span>;
};

export const QueryLogs: React.FC = () => {
  const { getAccessToken } = usePermissions();

  // Data state
  const [logs, setLogs] = useState<QueryLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [userFilter, setUserFilter] = useState<string>("");

  // Pagination state
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Expanded row state
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const fetchLogs = useCallback(
    async (page: number = 1) => {
      setIsLoading(true);
      setError(null);

      try {
        const accessToken = await getAccessToken();

        const params: QueryLogsParams = {
          page_size: 50,
          page,
        };

        if (startDate) {
          params.start_date = new Date(startDate).toISOString();
        }
        if (endDate) {
          // End of day for end date
          const endDateTime = new Date(endDate);
          endDateTime.setHours(23, 59, 59, 999);
          params.end_date = endDateTime.toISOString();
        }
        if (userFilter.trim()) {
          params.username = userFilter.trim();
        }

        const result = await getQueryLogs(accessToken, params);

        if (result.success) {
          setLogs(result.data.logs);
          setHasMore(result.data.has_more);
          setCurrentPage(page);
          if (page === 1) {
            setExpandedRowId(null);
          }
        } else {
          setError(result.error.message);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load query logs");
      } finally {
        setIsLoading(false);
      }
    },
    [getAccessToken, startDate, endDate, userFilter]
  );

  // Initial load
  useEffect(() => {
    fetchLogs(1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyFilters = () => {
    fetchLogs(1);
  };

  const handleNextPage = () => {
    if (hasMore) {
      fetchLogs(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      fetchLogs(currentPage - 1);
    }
  };

  const handleRowClick = (logId: string) => {
    setExpandedRowId(expandedRowId === logId ? null : logId);
  };

  const handleRefresh = () => {
    fetchLogs(1);
  };

  // Get citation count from response
  const getCitationCount = (log: QueryLogEntry): number => {
    if (log.metadata?.citation_count !== undefined) {
      return log.metadata.citation_count;
    }
    if (log.response?.citations) {
      return log.response.citations.length;
    }
    return 0;
  };

  return (
    <div className={styles.container}>
      <AppHeader currentApp="admin" />
      <main className={styles.main}>
        {/* Header */}
        <div className={styles.headerSection}>
          <h1 className={styles.title}>Query Logs</h1>
          <button
            className={styles.refreshButton}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <ArrowSyncRegular />
            <span>Refresh</span>
          </button>
        </div>

        {/* Filters Section */}
        <div className={styles.filtersSection}>
          <div className={styles.filterRow}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Start Date</label>
              <input
                type="date"
                className={styles.filterInput}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || formatDateForInput(new Date())}
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>End Date</label>
              <input
                type="date"
                className={styles.filterInput}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={formatDateForInput(new Date())}
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Username (optional)</label>
              <input
                type="text"
                className={styles.filterInput}
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                placeholder="Search by name or email"
              />
            </div>
            <button className={styles.applyButton} onClick={handleApplyFilters}>
              <FilterRegular />
              <span>Apply Filters</span>
            </button>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner size={SpinnerSize.large} label="Loading query logs..." />
          </div>
        ) : error ? (
          <div className={styles.errorContainer}>
            <p className={styles.errorMessage}>{error}</p>
            <button className={styles.retryButton} onClick={handleRefresh}>
              Retry
            </button>
          </div>
        ) : logs.length === 0 ? (
          <div className={styles.emptyContainer}>
            <h2 className={styles.emptyTitle}>No Query Logs Found</h2>
            <p className={styles.emptyText}>
              No chat interactions match the selected filters. Try adjusting the date
              range or clearing the user filter.
            </p>
          </div>
        ) : (
          <>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>User</th>
                    <th>Query</th>
                    <th>Answer (Preview)</th>
                    <th>Mode</th>
                    <th>Status</th>
                    <th>Response Time</th>
                    <th>Citations</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <React.Fragment key={log.id}>
                      <tr
                        onClick={() => handleRowClick(log.id)}
                        className={`${styles.clickableRow} ${
                          expandedRowId === log.id ? styles.expandedRow : ""
                        }`}
                      >
                        <td className={styles.timestampCell}>
                          {formatDate(log.timestamp)}
                        </td>
                        <td className={styles.userCell} title={log.username}>
                          {log.username || log.user_id}
                        </td>
                        <td className={styles.queryCell} title={log.request?.query}>
                          {truncateText(log.request?.query || "", 50)}
                        </td>
                        <td className={styles.answerCell} title={log.response?.answer}>
                          {truncateText(log.response?.answer || "", 60)}
                        </td>
                        <td>
                          <ModeBadge expertMode={log.request?.expert_mode || false} />
                        </td>
                        <td>
                          <StatusBadge
                            wasBlocked={log.response?.was_blocked || false}
                            wasClarification={log.response?.was_clarification || false}
                            hasError={!!log.response?.error}
                          />
                        </td>
                        <td>
                          {formatResponseTime(log.metadata?.response_time_ms || 0)}
                        </td>
                        <td>{getCitationCount(log)}</td>
                      </tr>
                      {expandedRowId === log.id && (
                        <tr className={styles.expandedContent}>
                          <td colSpan={8}>
                            <div className={styles.expandedDetails}>
                              <div className={styles.detailSection}>
                                <h4>Full Query</h4>
                                <div className={`${styles.contentBox} ${styles.query}`}>
                                  {log.request?.query || "N/A"}
                                </div>
                              </div>
                              <div className={styles.detailSection}>
                                <h4>Full Answer</h4>
                                <div className={`${styles.contentBox} ${styles.answer}`}>
                                  {log.response?.answer || "N/A"}
                                </div>
                              </div>
                              {log.response?.error && (
                                <div className={styles.detailSection}>
                                  <h4>Error</h4>
                                  <div className={`${styles.contentBox} ${styles.error}`}>
                                    {log.response.error}
                                  </div>
                                </div>
                              )}
                              {log.response?.citations && log.response.citations.length > 0 && (
                                <div className={styles.detailSection}>
                                  <h4>Citations ({log.response.citations.length})</h4>
                                  <div className={styles.citationsGrid}>
                                    {log.response.citations.map((citation: Citation, index: number) => (
                                      <div key={index} className={styles.citationCard}>
                                        <div className={styles.citationHeader}>
                                          <span className={styles.citationIndex}>[{index + 1}]</span>
                                          <span className={styles.citationTitle}>{citation.title}</span>
                                          {citation.metadata?.authority_label && (
                                            <span className={styles.authorityBadge}>
                                              {citation.metadata.authority_label}
                                            </span>
                                          )}
                                        </div>
                                        <div className={styles.citationContent}>
                                          {citation.content?.length > 300
                                            ? citation.content.slice(0, 300) + "..."
                                            : citation.content}
                                        </div>
                                        {citation.url && (
                                          <a
                                            href={citation.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.citationLink}
                                          >
                                            View Source
                                          </a>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div className={styles.metadataRow}>
                                <span>Model: {log.metadata?.model || "N/A"}</span>
                                <span>
                                  Search Results: {log.metadata?.search_results_count || 0}
                                </span>
                                <span>Conversation: {log.conversation_id}</span>
                                {log.request?.as_of_date && (
                                  <span>As-of Date: {log.request.as_of_date}</span>
                                )}
                                {log.request?.include_superseded && (
                                  <span>Include Superseded: Yes</span>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className={styles.pagination}>
              <button
                className={styles.paginationButton}
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
              >
                <ChevronLeftRegular />
                <span>Previous</span>
              </button>
              <span className={styles.pageInfo}>Page {currentPage}</span>
              <button
                className={styles.paginationButton}
                onClick={handleNextPage}
                disabled={!hasMore}
              >
                <span>Next</span>
                <ChevronRightRegular />
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
};
